import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GameSession, type SessionStatus } from '../game/session';
import {
  buildTutorialChart,
  getTutorialOverlays,
  type TutorialOverlay,
} from '../game/tutorialChart';
import { markTutorialSeen } from '../game/tutorialFlag';
import { useAuth } from '../auth/AuthProvider';
import { saveModePref } from '../game/modePref';
import { modePath } from '../game/modeRouting';
import './play.css';

const TUTORIAL_CHART = buildTutorialChart();

function pickOverlay(overlays: TutorialOverlay[], beat: number): TutorialOverlay {
  for (let i = overlays.length - 1; i >= 0; i--) {
    const o = overlays[i];
    if (beat >= o.fromBeat && beat < o.untilBeat) {
      return o;
    }
  }
  return overlays[0];
}

export function TutorialPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sessionRef = useRef<GameSession | null>(null);
  const [needsTap, setNeedsTap] = useState(true);
  const [status, setStatus] = useState<SessionStatus>('idle');
  // Pointer-coarse devices get tap-worded instructions and a layout that
  // keeps the overlay below the canvas (see play.css). We sample once at
  // mount; this rarely changes mid-session and re-mounting picks up any
  // change.
  const isTouch = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches,
    [],
  );
  const overlays = useMemo(() => getTutorialOverlays(isTouch), [isTouch]);
  const [overlay, setOverlay] = useState<TutorialOverlay>(overlays[0]);

  useEffect(() => {
    return () => {
      if (sessionRef.current) {
        void sessionRef.current.destroy();
        sessionRef.current = null;
      }
    };
  }, []);

  // Tutorial today is the Pulse tutorial (Phase 6 will add a Circles
  // tutorial at /circles/tutorial). Persist the mode so a player who
  // skips the tutorial doesn't get punted back to the picker on next
  // visit.
  useEffect(() => {
    saveModePref('pulse');
  }, []);

  useEffect(() => {
    let raf = 0;
    const loop = () => {
      const session = sessionRef.current;
      if (session) {
        const beat = session.getCurrentBeat();
        if (Number.isFinite(beat)) {
          const next = pickOverlay(overlays, beat);
          setOverlay((cur) => (cur.fromBeat === next.fromBeat ? cur : next));
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [overlays]);

  const startTutorial = useCallback(async () => {
    if (!canvasRef.current) return;
    setNeedsTap(false);
    if (sessionRef.current) {
      await sessionRef.current.destroy();
      sessionRef.current = null;
    }
    const session = new GameSession({
      chart: TUTORIAL_CHART,
      canvas: canvasRef.current,
      audioOffsetMs: user?.audioOffsetMs ?? 0,
      onStatusChange: (s) => setStatus(s),
      onComplete: (outcome) => {
        // Only mark the tutorial seen when the player actually
        // completed it; a failed first attempt should still route them
        // back here next time they hit `/pulse` (the home page gates on
        // this flag).
        if (outcome.completed) {
          markTutorialSeen('pulse');
        }
      },
      firstTime: true,
    });
    sessionRef.current = session;
    try {
      await session.start();
    } catch (err) {
      console.error('Failed to start tutorial:', err);
      setNeedsTap(true);
    }
  }, [user?.audioOffsetMs]);

  useEffect(() => {
    if (!needsTap) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === ' ' || e.code === 'Space' || e.key === 'Enter') {
        e.preventDefault();
        void startTutorial();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [needsTap, startTutorial]);

  const skipTutorial = () => {
    markTutorialSeen('pulse');
    navigate(modePath('pulse'));
  };

  return (
    <div className="play-page tutorial-page" data-testid="tutorial-page">
      <div className="play-canvas-wrap">
        <canvas ref={canvasRef} className="play-canvas" data-testid="tutorial-canvas" />

        {needsTap && (
          <div className="play-overlay" data-testid="tutorial-start-overlay">
            <div className="play-overlay-card">
              <h2>Welcome to BeatHeaven</h2>
              <HowToPlayDemo />
              <p>{isTouch ? 'Tap' : 'Press SPACE'} when a pad meets your cube — you flip to the opposite rail.</p>
              <p className="muted faint">30-second tutorial · headphones recommended.</p>
              <div className="play-overlay-actions">
                <button
                  type="button"
                  className="btn btn-primary btn-large"
                  onClick={() => void startTutorial()}
                  data-testid="tutorial-start"
                >
                  Start tutorial
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={skipTutorial}
                  data-testid="tutorial-skip"
                >
                  Skip — I know how to play
                </button>
              </div>
            </div>
          </div>
        )}

        {status === 'completed' && (
          <div className="play-overlay" data-testid="tutorial-complete-overlay">
            <div className="play-overlay-card">
              <h2>You're ready</h2>
              <p>Pick a real track from the home screen and dash.</p>
              <div className="play-overlay-actions">
                <Link to={modePath('pulse')} className="btn btn-primary btn-large">
                  Back to home
                </Link>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    setNeedsTap(true);
                    setStatus('idle');
                  }}
                >
                  Replay tutorial
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/*
       * Sibling of the canvas wrap (not a child) so on narrow viewports the
       * overlay flows below the playfield instead of covering it. On wider
       * viewports CSS lifts it back into a floating overlay anchored to the
       * canvas — see .tutorial-overlay in play.css.
       *
       * Hide once the run completes: the completion overlay (.play-overlay)
       * lives inside .play-canvas-wrap and only covers the canvas, so on
       * mobile (where this overlay is a sibling sitting *below* the canvas)
       * leaving it visible would show stale instructions next to the
       * "You're ready" card.
       */}
      {!needsTap && status !== 'completed' && (
        <div className="tutorial-overlay" data-testid="tutorial-overlay-text">
          <div className="tutorial-overlay-card">
            <h3>{overlay.title}</h3>
            <p>{overlay.body}</p>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Looped 3-second SVG demo shown above the "Start tutorial" button. A
 * pad scrolls in from the RIGHT toward the player column on the left;
 * on arrival the cube flips from floor to ceiling and a "HIT!" pops.
 * Mirrors the live playfield (player on the left, pads scroll right→
 * left) so the demo sets the same mental model the player will see
 * when the tutorial starts. All animation lives in play.css keyframes;
 * the SVG geometry here is intentionally simple so the timing is easy
 * to read and tune.
 */
function HowToPlayDemo() {
  return (
    <svg
      viewBox="0 0 320 110"
      className="tutorial-demo"
      role="img"
      aria-label="Demo: tap when a pad meets your cube and you flip to the opposite rail"
      data-testid="tutorial-how-to-demo"
    >
      <defs>
        <linearGradient id="td-bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#0a0e25" />
          <stop offset="1" stopColor="#14193a" />
        </linearGradient>
      </defs>
      <rect width="320" height="110" fill="url(#td-bg)" />
      <g className="tutorial-demo-stars">
        <circle cx="50" cy="48" r="0.9" />
        <circle cx="130" cy="74" r="0.7" />
        <circle cx="220" cy="44" r="0.8" />
        <circle cx="280" cy="68" r="0.6" />
      </g>
      <rect className="tutorial-demo-rail" x="0" y="20" width="320" height="2" />
      <rect className="tutorial-demo-rail" x="0" y="88" width="320" height="2" />
      <line className="tutorial-demo-receptor" x1="100" y1="22" x2="100" y2="88" />
      <text className="tutorial-demo-receptor-label" x="100" y="105" textAnchor="middle">
        TAP HERE
      </text>
      <g className="tutorial-demo-pad-scroll">
        <circle className="tutorial-demo-ring" cx="0" cy="22" r="14" />
        <rect className="tutorial-demo-pad" x="-12" y="18" width="24" height="5" rx="1" />
      </g>
      <g className="tutorial-demo-cube-y">
        <rect className="tutorial-demo-cube" x="92" y="-8" width="16" height="16" rx="2" />
        <polygon className="tutorial-demo-cube-arrow" points="100,-3 96,3 104,3" />
      </g>
      <text className="tutorial-demo-hit" x="100" y="56" textAnchor="middle">
        HIT!
      </text>
      <g className="tutorial-demo-tap-callout">
        <rect x="120" y="44" width="42" height="20" rx="4" />
        <text x="141" y="58" textAnchor="middle">
          TAP!
        </text>
      </g>
    </svg>
  );
}
